import { useTranslation } from "@sagot/i18n";
import { Field, Input, Segmented, Select } from "@sagot/ui";
import type { ReactNode } from "react";
import { Controller, useFormContext, type FieldPath } from "react-hook-form";
import { SettingSwitch } from "./setting-row";
import { WIDGET_LIMITS, type WidgetForm } from "./widget-model";

const K = "deployKnowledge.widgetStudio.behavior";

type BooleanPath = Extract<
  FieldPath<WidgetForm>,
  "behavior.auto_open.enabled" | "behavior.proactive.enabled" | "behavior.show_on_mobile" | "behavior.sound"
>;

function SwitchField({ name, title, hint, disabled, children, className }: { name: BooleanPath; title: string; hint?: string; disabled: boolean; children?: ReactNode; className?: string }) {
  const { control } = useFormContext<WidgetForm>();
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <SettingSwitch className={className} title={title} hint={hint} checked={field.value} onCheckedChange={field.onChange} disabled={disabled}>
          {children}
        </SettingSwitch>
      )}
    />
  );
}

function LeadCapture() {
  const { t } = useTranslation();
  const { control, watch } = useFormContext<WidgetForm>();
  const mode = watch("behavior.lead_capture");
  const hint = { off: t(`${K}.leadHintOff`), optional: t(`${K}.leadHintOptional`), required: t(`${K}.leadHintRequired`) }[mode];
  return (
    <div className="flex flex-col gap-2 p-4">
      <div className="text-sm font-semibold">{t(`${K}.leadCapture`)}</div>
      <Controller
        control={control}
        name="behavior.lead_capture"
        render={({ field }) => (
          <Segmented
            className="self-start"
            aria-label={t(`${K}.leadCapture`)}
            value={field.value}
            onValueChange={field.onChange}
            options={[
              { value: "off", label: t(`${K}.leadOff`) },
              { value: "optional", label: t(`${K}.leadOptional`) },
              { value: "required", label: t(`${K}.leadRequired`) },
            ]}
          />
        )}
      />
      <p className="text-xs text-fg-subtle">{hint}</p>
    </div>
  );
}

export function BehaviorPanel({ disabled }: { disabled: boolean }) {
  const { t } = useTranslation();
  const { register } = useFormContext<WidgetForm>();
  const delay = { valueAsNumber: true } as const;
  return (
    <div className="space-y-5">
      <div className="rounded-lg border border-line p-4">
        <SwitchField name="behavior.auto_open.enabled" title={t(`${K}.autoOpen`)} hint={t(`${K}.autoOpenHint`)} disabled={disabled}>
          <div className="flex items-center gap-2 text-sm">
            <span aria-hidden="true">{t(`${K}.after`)}</span>
            <Input
              type="number"
              className="h-9 w-20"
              min={1}
              max={WIDGET_LIMITS.delayMaxS}
              aria-label={t(`${K}.autoOpenDelay`)}
              {...register("behavior.auto_open.delay_s", delay)}
            />
            <span aria-hidden="true">{t(`${K}.seconds`)}</span>
          </div>
        </SwitchField>
      </div>
      <div className="rounded-lg border border-line p-4">
        <SwitchField name="behavior.proactive.enabled" title={t(`${K}.proactive`)} hint={t(`${K}.proactiveHint`)} disabled={disabled}>
          <div className="space-y-3">
            <Field label={t(`${K}.proactiveMessage`)}>
              <Input maxLength={120} {...register("behavior.proactive.message")} />
            </Field>
            <div className="grid grid-cols-[1fr_8rem] gap-3">
              <Field label={t(`${K}.proactivePages`)} hint={t(`${K}.proactivePagesHint`)}>
                <Input className="font-mono text-xs" {...register("behavior.proactive.url_patterns")} />
              </Field>
              <Field label={t(`${K}.proactiveDelay`)}>
                <Input type="number" min={0} max={WIDGET_LIMITS.delayMaxS} {...register("behavior.proactive.delay_s", delay)} />
              </Field>
            </div>
          </div>
        </SwitchField>
      </div>
      <div className="divide-y divide-line rounded-lg border border-line">
        <SwitchField className="p-4" name="behavior.show_on_mobile" title={t(`${K}.showOnMobile`)} disabled={disabled} />
        <LeadCapture />
        <div className="p-4">
          <Field label={t(`${K}.outOfHours`)}>
            <Select {...register("behavior.out_of_hours")}>
              <option value="callback">{t(`${K}.outOfHoursCallback`)}</option>
              <option value="hide_handoff">{t(`${K}.outOfHoursHide`)}</option>
              <option value="offline">{t(`${K}.outOfHoursOffline`)}</option>
            </Select>
          </Field>
        </div>
        <SwitchField className="p-4" name="behavior.sound" title={t(`${K}.sound`)} hint={t(`${K}.soundHint`)} disabled={disabled} />
        <div className="flex items-center justify-between gap-4 p-4">
          <div>
            <div className="text-sm font-semibold">{t(`${K}.remember`)}</div>
            <div className="text-xs text-fg-subtle">{t(`${K}.rememberHint`)}</div>
          </div>
          <Input
            type="number"
            className="h-9 w-20"
            min={0}
            max={WIDGET_LIMITS.rememberMaxDays}
            aria-label={t(`${K}.rememberDays`)}
            {...register("behavior.remember_days", delay)}
          />
        </div>
      </div>
    </div>
  );
}
